import React, { useState, useEffect } from 'react';
import {
    Container,
    Card,
    Form,
    Button,
    Spinner,
    Alert,
    Badge,
} from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import surveyService from '@/shared/services/surveyService';
import './surveys.css';

const LIKERT_LABELS = [
    'Strongly Disagree',
    'Disagree',
    'Neutral',
    'Agree',
    'Strongly Agree',
];

function SurveyFormPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [survey, setSurvey] = useState(null);
    const [responses, setResponses] = useState({});
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});

    useEffect(() => {
        const loadSurvey = async () => {
            try {
                setLoading(true);
                setError(null);
                const data = await surveyService.getSurveyById(id);
                setSurvey(data);

                // Initialize responses
                const initial = {};
                for (const q of data.questions || []) {
                    if (q.type === 'multiselect') {
                        initial[q.questionId] = [];
                    } else if (q.type === 'checkbox') {
                        initial[q.questionId] = false;
                    } else {
                        initial[q.questionId] = '';
                    }
                }
                setResponses(initial);
            } catch (_err) {
                setError('Failed to load survey. Please try again.');
            } finally {
                setLoading(false);
            }
        };

        loadSurvey();
    }, [id]);

    const handleChange = (questionId, value) => {
        setResponses((prev) => ({ ...prev, [questionId]: value }));
        // Clear validation error when user provides input
        if (validationErrors[questionId]) {
            setValidationErrors((prev) => {
                const next = { ...prev };
                delete next[questionId];
                return next;
            });
        }
    };

    const handleMultiselectChange = (questionId, option, checked) => {
        setResponses((prev) => {
            const current = prev[questionId] || [];
            if (checked) {
                return { ...prev, [questionId]: [...current, option] };
            }
            return {
                ...prev,
                [questionId]: current.filter((o) => o !== option),
            };
        });
        if (validationErrors[questionId]) {
            setValidationErrors((prev) => {
                const next = { ...prev };
                delete next[questionId];
                return next;
            });
        }
    };

    const validate = () => {
        const errors = {};
        for (const q of survey.questions || []) {
            if (!q.required) continue;

            const value = responses[q.questionId];
            if (q.type === 'multiselect') {
                if (!value || value.length === 0) {
                    errors[q.questionId] = 'This question is required';
                }
            } else if (q.type === 'checkbox') {
                // Checkbox required means it must be checked
                if (!value) {
                    errors[q.questionId] = 'This field must be checked';
                }
            } else {
                if (value === '' || value === null || value === undefined) {
                    errors[q.questionId] = 'This question is required';
                }
            }
        }
        return errors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const errors = validate();
        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors);
            setError('Please answer all required questions before submitting.');
            return;
        }

        try {
            setSubmitting(true);
            setError(null);
            await surveyService.submitResponse(id, {
                responses,
                anonymous: survey?.allowAnonymous ?? false,
            });
            setSuccess(true);
            setTimeout(() => {
                navigate('/surveys');
            }, 2000);
        } catch (err) {
            if (err.response?.status === 409) {
                setError('You have already responded to this survey.');
            } else {
                const message =
                    err.response?.data?.message ||
                    'Failed to submit response. Please try again.';
                setError(message);
            }
        } finally {
            setSubmitting(false);
        }
    };

    const renderQuestion = (question) => {
        const qId = question.questionId;
        const hasError = !!validationErrors[qId];

        return (
            <Card
                key={qId}
                className={`mb-3 border-0 shadow-sm ${hasError ? 'border border-danger' : ''}`}
            >
                <Card.Body>
                    <Form.Group controlId={qId}>
                        {/*
                          The grouped question types below name their group from
                          this label. controlId wires the label to a single
                          control by id, which the radio and checkbox sets have
                          no equivalent of, so they carry aria-labelledby instead.
                        */}
                        <Form.Label id={`${qId}-label`} className="fw-bold">
                            {question.text}
                            {question.required && (
                                <span className="text-danger ms-1">*</span>
                            )}
                        </Form.Label>
                        {question.helpText && (
                            <Form.Text className="d-block mb-2 text-muted">
                                {question.helpText}
                            </Form.Text>
                        )}

                        {question.type === 'text' && (
                            <Form.Control
                                type="text"
                                value={responses[qId] || ''}
                                onChange={(e) =>
                                    handleChange(qId, e.target.value)
                                }
                                isInvalid={hasError}
                            />
                        )}

                        {question.type === 'textarea' && (
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={responses[qId] || ''}
                                onChange={(e) =>
                                    handleChange(qId, e.target.value)
                                }
                                isInvalid={hasError}
                            />
                        )}

                        {question.type === 'likert' && (
                            <div
                                role="radiogroup"
                                aria-labelledby={`${qId}-label`}
                                className="d-flex justify-content-between align-items-center mt-2"
                            >
                                {LIKERT_LABELS.map((label, idx) => {
                                    const value = idx + 1;
                                    return (
                                        <div
                                            key={value}
                                            className="text-center"
                                        >
                                            <Form.Check
                                                type="radio"
                                                id={`${qId}-${value}`}
                                                name={qId}
                                                value={value}
                                                aria-label={label}
                                                checked={
                                                    Number(responses[qId]) ===
                                                    value
                                                }
                                                onChange={() =>
                                                    handleChange(qId, value)
                                                }
                                                isInvalid={hasError}
                                                className="d-flex justify-content-center"
                                            />
                                            <small className="text-muted d-block mt-1">
                                                {label}
                                            </small>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {question.type === 'rating' && (
                            <div
                                role="radiogroup"
                                aria-labelledby={`${qId}-label`}
                                className="d-flex gap-3 mt-2"
                            >
                                {[1, 2, 3, 4, 5].map((value) => (
                                    <Form.Check
                                        key={value}
                                        type="radio"
                                        id={`${qId}-${value}`}
                                        name={qId}
                                        label={value}
                                        value={value}
                                        checked={
                                            Number(responses[qId]) === value
                                        }
                                        onChange={() =>
                                            handleChange(qId, value)
                                        }
                                        isInvalid={hasError}
                                        inline
                                    />
                                ))}
                            </div>
                        )}

                        {question.type === 'select' && (
                            <Form.Select
                                value={responses[qId] || ''}
                                onChange={(e) =>
                                    handleChange(qId, e.target.value)
                                }
                                isInvalid={hasError}
                            >
                                <option value="">Select an option...</option>
                                {(question.options || []).map((opt) => (
                                    <option key={opt} value={opt}>
                                        {opt}
                                    </option>
                                ))}
                            </Form.Select>
                        )}

                        {question.type === 'multiselect' && (
                            <div
                                role="group"
                                aria-labelledby={`${qId}-label`}
                                className="mt-1"
                            >
                                {(question.options || []).map((opt) => (
                                    <Form.Check
                                        key={opt}
                                        type="checkbox"
                                        id={`${qId}-${opt}`}
                                        label={opt}
                                        checked={(
                                            responses[qId] || []
                                        ).includes(opt)}
                                        onChange={(e) =>
                                            handleMultiselectChange(
                                                qId,
                                                opt,
                                                e.target.checked
                                            )
                                        }
                                        isInvalid={hasError}
                                        className="mb-1"
                                    />
                                ))}
                            </div>
                        )}

                        {question.type === 'checkbox' && (
                            <Form.Check
                                type="checkbox"
                                id={qId}
                                label="Yes"
                                checked={!!responses[qId]}
                                onChange={(e) =>
                                    handleChange(qId, e.target.checked)
                                }
                                isInvalid={hasError}
                            />
                        )}

                        {hasError && (
                            <Form.Control.Feedback
                                type="invalid"
                                className="d-block"
                            >
                                {validationErrors[qId]}
                            </Form.Control.Feedback>
                        )}
                    </Form.Group>
                </Card.Body>
            </Card>
        );
    };

    if (loading) {
        return (
            <Container className="py-5 text-center">
                <Spinner animation="border" role="status" variant="primary">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
                <p className="mt-3 text-muted">Loading survey...</p>
            </Container>
        );
    }

    if (!survey) {
        return (
            <Container className="py-5 text-center">
                <Alert variant="danger">Survey not found.</Alert>
            </Container>
        );
    }

    if (survey.hasResponded) {
        return (
            <Container className="py-4">
                <Card className="border-0 shadow-sm">
                    <Card.Body className="text-center py-5">
                        <Badge bg="success" className="mb-3 fs-6">
                            Completed
                        </Badge>
                        <h4>{survey.title}</h4>
                        <p className="text-muted">
                            You have already completed this survey. Thank you
                            for your feedback.
                        </p>
                        <Button
                            variant="primary"
                            onClick={() => navigate('/surveys')}
                        >
                            Back to Surveys
                        </Button>
                    </Card.Body>
                </Card>
            </Container>
        );
    }

    if (success) {
        return (
            <Container className="py-4">
                <Alert variant="success" className="text-center py-4">
                    <h5>Thank you for completing this survey!</h5>
                    <p className="mb-0">
                        Your response has been recorded. Redirecting to
                        surveys...
                    </p>
                </Alert>
            </Container>
        );
    }

    return (
        <Container className="py-4 survey-form-container">
            <div className="mb-4">
                <Button
                    variant="link"
                    className="p-0 text-muted"
                    onClick={() => navigate('/surveys')}
                >
                    &larr; Back to Surveys
                </Button>
            </div>

            <Card className="border-0 shadow-sm mb-4">
                <Card.Body>
                    <h2>{survey.title}</h2>
                    {survey.description && (
                        <p className="text-muted">{survey.description}</p>
                    )}
                    <div className="d-flex gap-2">
                        {survey.allowAnonymous && (
                            <Badge bg="light" text="dark" className="border">
                                Anonymous responses allowed
                            </Badge>
                        )}
                        <Badge bg="light" text="dark" className="border">
                            {survey.questions?.length || 0} questions
                        </Badge>
                    </div>
                </Card.Body>
            </Card>

            {error && (
                <Alert
                    variant="danger"
                    dismissible
                    onClose={() => setError(null)}
                >
                    {error}
                </Alert>
            )}

            <Form noValidate onSubmit={handleSubmit}>
                {(survey.questions || []).map(renderQuestion)}

                <div className="d-flex justify-content-between mt-4">
                    <Button
                        variant="outline-primary"
                        onClick={() => navigate('/surveys')}
                        disabled={submitting}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="primary"
                        disabled={submitting}
                    >
                        {submitting ? (
                            <>
                                <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    className="me-2"
                                />
                                Submitting...
                            </>
                        ) : (
                            'Submit Response'
                        )}
                    </Button>
                </div>
            </Form>
        </Container>
    );
}

export default SurveyFormPage;
